import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type Editor } from 'slate';
import type AhaEditorV2Controller from '../../controllers/AhaEditorV2Controller';
import Menu from './Menu';
import { type MenuItemComponentType, type OnSearchFunc } from './types';

// Debounce time for search results
const DEBOUNCE_TIME = 200;

interface VisibleMenuProps<MenuItemObject, SearchResult> {
  editor: Editor;
  controller: AhaEditorV2Controller;
  hideMenu: () => void;
  searchText: string | null;
  setKeydownHandler: (
    handler: ((event: KeyboardEvent) => boolean | undefined) | null
  ) => void;
  name: string;
  onSearch: OnSearchFunc<SearchResult>;
  onSelect: ({
    item,
    event,
  }: {
    item: MenuItemObject;
    event: MouseEvent | KeyboardEvent;
  }) => void;
  MenuItemComponent: MenuItemComponentType<MenuItemObject>;
  menuItemUniqueKey: keyof MenuItemObject;
  searchingPlaceholder?: string;
  classNames?: {
    menu?: string;
    menuItem?: string;
  };
  searchResults: Map<string, any>;
  lastMenuItems: Map<string, any>;
}

const VisibleMenuWrapper = <MenuItemObject, SearchResult>({
  editor,
  controller,
  hideMenu,
  searchText,
  setKeydownHandler,
  name,
  onSearch,
  onSelect,
  MenuItemComponent,
  menuItemUniqueKey,
  classNames,
  searchingPlaceholder = 'Searching...',
  searchResults,
  lastMenuItems,
}: VisibleMenuProps<MenuItemObject, SearchResult>) => {
  const [menuWrapperElement, setMenuWrapperElement] =
    useState<HTMLElement | null>(null);

  const [mouseMoving, setMouseMoving] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [menuItemsLoading, setMenuItemsLoading] = useState<boolean>(false);
  const [menuItems, setMenuItems] = useState<SearchResult[]>(
    lastMenuItems.get(name) || []
  );

  const onMouseOverIndex = (index: number) => {
    if (mouseMoving) {
      setSelectedIndex(index);
    }
  };

  const onSelectItem = useCallback(
    (index: number, event: KeyboardEvent | MouseEvent) => {
      const flattenedItems = menuItems.flat() as MenuItemObject[];
      if (index >= flattenedItems.length) return;

      event.preventDefault();
      event.stopPropagation();

      onSelect({
        item: flattenedItems[index],
        event,
      });
    },
    [menuItems]
  );

  const onResults = useCallback(
    (results: SearchResult[]) => {
      setMenuItems(results);
      lastMenuItems.set(name, results);
      setMenuItemsLoading(false);
      setSelectedIndex(0);
      if (results.length === 0 && searchText && /\s$/.test(searchText)) {
        hideMenu();
      }
    },
    [hideMenu, searchText, name]
  );

  // Ensures that the debounced search is stable as long as
  // onSearch and onResults props don't change
  const debouncedSearch = useCallback(
    debounce(
      (text: string) => {
        setMenuItemsLoading(true);
        onSearch({ text, editor, controller })
          .then((results) => {
            if (!Array.isArray(results)) {
              results = [];
            }
            onResults(results);
            searchResults.set(`${name}__${text}`, results);
          })
          .catch((err) => {
            if (err.message !== 'aborted') {
              console.error(err);
            }
          });
      },
      DEBOUNCE_TIME,
      { leading: true }
    ),
    [onSearch, onResults, editor, controller]
  );

  useEffect(() => {
    if (!debouncedSearch) return;
    if (searchText === null) return;

    if (searchResults.has(`${name}__${searchText}`)) {
      onResults(searchResults.get(`${name}__${searchText}`));
      return;
    }

    debouncedSearch(searchText);

    return () => debouncedSearch?.cancel();
  }, [debouncedSearch, onResults, searchText, name, searchResults]);

  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      const flattenedItems = menuItems.flat() as MenuItemObject[];

      // If there are no results and the user presses a navigational key,
      // we should hide the menu and let the default behavior take over.
      if (
        flattenedItems.length === 0 &&
        ['ArrowDown', 'ArrowUp', 'Enter', 'Tab'].includes(event.key)
      ) {
        hideMenu();
        return;
      }

      const lastIndex = flattenedItems.length - 1;

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          setMouseMoving(false);
          setSelectedIndex(selectedIndex === lastIndex ? 0 : selectedIndex + 1);
          return true;
        }
        case 'ArrowUp': {
          event.preventDefault();
          setMouseMoving(false);
          setSelectedIndex(selectedIndex === 0 ? lastIndex : selectedIndex - 1);
          return true;
        }
        case 'Enter':
        case 'Tab': {
          event.preventDefault();
          onSelectItem(selectedIndex, event);
          return true;
        }
        default:
          break;
      }
    };

    setKeydownHandler(keydown);

    return () => {
      setKeydownHandler(null);
    };
  }, [selectedIndex, menuItems, setKeydownHandler, onSelectItem]);

  useEffect(() => {
    const mouseMoveHandler = () => {
      setMouseMoving(true);
    };

    window.addEventListener('mousemove', mouseMoveHandler);
    window.addEventListener('wheel', mouseMoveHandler);

    return () => {
      window.removeEventListener('mousemove', mouseMoveHandler);
      window.removeEventListener('wheel', mouseMoveHandler);
    };
  }, []);

  const observer = useMemo(() => {
    const options = {
      root: menuWrapperElement,
      rootMargin: '0px',
      threshold: 1.0,
    };

    const onIntersectionChanged = ([
      intersectionEntry,
    ]: IntersectionObserverEntry[]) => {
      // Ensure the user is not doing operations with the mouse,
      // otherwise it makes it impossible to scroll with the scroll bar.
      if (!intersectionEntry.isIntersecting && !mouseMoving) {
        const ahaMenuItem = intersectionEntry.target.closest('aha-menu-item');
        (ahaMenuItem ?? intersectionEntry.target).scrollIntoView({
          block: 'nearest',
        });
      }
    };

    return new window.IntersectionObserver(onIntersectionChanged, options);
  }, [menuWrapperElement, mouseMoving]);

  return (
    <div ref={(el) => setMenuWrapperElement(el)}>
      <Menu
        menuItemsLoading={menuItemsLoading}
        observer={observer}
        MenuItemComponent={MenuItemComponent}
        menuItems={menuItems}
        menuItemUniqueKey={menuItemUniqueKey}
        selectedIndex={selectedIndex}
        onSelectItem={onSelectItem}
        onMouseOverIndex={onMouseOverIndex}
        onMouseMove={() => setMouseMoving(true)}
        classNames={classNames}
        searchingPlaceholder={searchingPlaceholder}
      />
    </div>
  );
};

export default VisibleMenuWrapper;
